import { useMutation, useQuery } from '@tanstack/react-query';

import { quoteFinancial } from '@/shared/api/resources';
import { formatCivilDate } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { SkeletonText } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/useToast';

import { descricaoDaParcela } from '../../portfolio/apolice';
import {
  abrirBoleto,
  chaveDasParcelasDaCotacao,
  motivoDaCotacaoEncerrada,
  MOTIVO_SEM_BOLETO,
  temBoletoNaCotacao,
} from '../../portfolio/boletoAnexado';

/**
 * "Baixar boleto(s)" da cotacao (NCT-057; DownloadBankSlip e GetProposalInstallments do
 * OnPoint): as parcelas da cotacao com o boleto que a seguradora anexou a cada uma, e um
 * "Download do boleto" por parcela.
 *
 * Serve a corretora, na linha do tempo e no menu da lista, e a aba Apolices do tomador
 * para a operacao que aguarda emissao. So le: anexar, trocar e retirar o boleto ficam
 * com a seguradora, no Resumo da cotacao. O endereco temporario e pedido no clique,
 * porque cada entrega entra na trilha (P-BOL; ADR 0071).
 *
 * Na cotacao encerrada o download fica desligado com o motivo, como no servidor:
 * depois da emissao o boleto e o da apolice, e a cotacao recusada ou cancelada nao tem
 * premio a pagar (R5-A08).
 */
export function QuoteBilletsDialog({
  operationId,
  numero,
  open,
  onClose,
}: {
  operationId: string;
  /** Numero da cotacao no titulo. */
  numero: string;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const parcelas = useQuery({
    queryKey: chaveDasParcelasDaCotacao(operationId),
    queryFn: ({ signal }) => quoteFinancial.installments(operationId, signal),
    enabled: open && operationId !== '',
  });
  const baixar = useMutation({
    mutationFn: (parcela: number) => quoteFinancial.billet(operationId, parcela),
    onSuccess: (boleto) => {
      if (!boleto.available) {
        toast.error(boleto.reason || MOTIVO_SEM_BOLETO);
        return;
      }
      abrirBoleto(boleto.url);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const lista = parcelas.data;
  const anexado = lista?.billet_mode === 'anexo';
  const encerrada = motivoDaCotacaoEncerrada(lista?.stage);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Boletos"
      description={`Cotacao ${numero}`}
      size="md"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      {parcelas.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as parcelas">
          {parcelas.error.message}
        </Alert>
      ) : parcelas.isPending ? (
        <SkeletonText lines={3} />
      ) : lista === undefined || lista.items.length === 0 ? (
        <p className="text-sm text-ink-600">
          Nenhuma parcela informada pela seguradora nesta cotacao. {MOTIVO_SEM_BOLETO}
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-2xs font-semibold uppercase tracking-wide text-ink-500">
              <th scope="col" className="py-2">
                Descricao
              </th>
              <th scope="col" className="py-2">
                Vencimento
              </th>
              <th scope="col" className="py-2">
                Boleto
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {lista.items.map((parcela) => {
              const disponivel =
                anexado && encerrada === null && parcela.billet !== null;
              return (
                <tr key={parcela.id}>
                  <td className="py-2 text-ink-800">
                    {descricaoDaParcela(
                      parcela.number,
                      parcela.amount,
                      parcela.interest,
                    )}
                  </td>
                  <td className="py-2 tabular-nums">
                    {formatCivilDate(parcela.due_on)}
                  </td>
                  <td className="py-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!disponivel}
                      title={disponivel ? undefined : (encerrada ?? MOTIVO_SEM_BOLETO)}
                      loading={baixar.isPending && baixar.variables === parcela.number}
                      aria-label={`Download do boleto da parcela ${parcela.number}`}
                      onClick={() => baixar.mutate(parcela.number)}
                    >
                      Download do boleto
                    </Button>
                    {parcela.billet?.number ? (
                      <p className="mt-1 text-2xs tabular-nums text-ink-500">
                        {parcela.billet.number}
                      </p>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {lista && lista.items.length > 0 && !temBoletoNaCotacao(lista) ? (
        <p className="mt-4 text-xs text-ink-500">{encerrada ?? MOTIVO_SEM_BOLETO}</p>
      ) : null}
      {lista?.single_billet ? (
        <p className="mt-4 text-xs text-ink-500">
          Boleto unico para todas as parcelas.
        </p>
      ) : null}
    </Modal>
  );
}
